import { useCallback, useEffect, useMemo, useState } from 'react'
import { Bell, CalendarDays, Clock, Loader2, Scissors, Wallet } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { formatArrivalTime } from '@/lib/calendar/checkIn'
import {
  fetchMyCommissionSummary,
  fetchMyTipsSummary,
  fetchStylistLive,
  fetchStylistSchedule,
  markStylistNotificationRead,
  requestStylistBlockout,
  updateStylistServiceStatus,
} from '@/lib/api/stylistWorkspace'
import { formatReportMoney, REPORT_PERIOD_OPTIONS, reportPeriodBounds } from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { cn } from '@/lib/utils'

const TABS = [
  { id: 'today', label: 'Today', icon: CalendarDays },
  { id: 'tips', label: 'Tips', icon: Wallet },
  { id: 'commissions', label: 'Pay', icon: Wallet },
  { id: 'time-off', label: 'Time off', icon: Clock },
]

export function StylistWorkspacePage() {
  const { activeBranchId } = useBranch()
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab')
  const [tab, setTab] = useState(
    TABS.some((item) => item.id === initialTab) ? initialTab : 'today',
  )
  const [schedule, setSchedule] = useState(null)
  const [live, setLive] = useState(null)
  const [tips, setTips] = useState(null)
  const [commissions, setCommissions] = useState(null)
  const [period, setPeriod] = useState('month')
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState('')
  const [error, setError] = useState('')
  const [blockoutForm, setBlockoutForm] = useState({
    title: 'Time off',
    starts_at: '',
    ends_at: '',
    notes: '',
  })

  const bounds = useMemo(() => reportPeriodBounds(period), [period])
  const pollSeconds = live?.poll_seconds ?? schedule?.operations?.stylist_workspace_poll_seconds ?? 15

  const loadSchedule = useCallback(async () => {
    setError('')
    try {
      const data = await fetchStylistSchedule()
      setSchedule(data)
    } catch (err) {
      setSchedule(null)
      setError(apiErrorMessage(err, 'Unable to load your schedule.'))
    }
  }, [])

  const loadLive = useCallback(async () => {
    try {
      const data = await fetchStylistLive()
      setLive(data)
    } catch {
      setLive(null)
    }
  }, [])

  const loadTips = useCallback(async () => {
    try {
      const data = await fetchMyTipsSummary({ from: bounds.from, to: bounds.to })
      setTips(data)
    } catch (err) {
      setTips(null)
      setError(apiErrorMessage(err, 'Unable to load tips.'))
    }
  }, [bounds.from, bounds.to])

  const loadCommissions = useCallback(async () => {
    try {
      const data = await fetchMyCommissionSummary({ from: bounds.from, to: bounds.to })
      setCommissions(data)
    } catch (err) {
      setCommissions(null)
      setError(apiErrorMessage(err, 'Unable to load commissions.'))
    }
  }, [bounds.from, bounds.to])

  useEffect(() => {
    setLoading(true)
    Promise.all([loadSchedule(), loadLive()]).finally(() => setLoading(false))
  }, [loadSchedule, loadLive, activeBranchId])

  useEffect(() => {
    if (tab === 'tips') loadTips()
    if (tab === 'commissions') loadCommissions()
  }, [tab, loadTips, loadCommissions, activeBranchId])

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadLive()
      if (tab === 'today') loadSchedule()
    }, pollSeconds * 1000)

    return () => window.clearInterval(timer)
  }, [loadLive, loadSchedule, pollSeconds, tab])

  const handleServiceStatus = async (appointmentId, serviceLineId, status) => {
    const key = `${appointmentId}-${serviceLineId}-${status}`
    setUpdatingId(key)
    setError('')
    try {
      await updateStylistServiceStatus(appointmentId, serviceLineId, status)
      await Promise.all([loadSchedule(), loadLive()])
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to update service status.'))
    } finally {
      setUpdatingId('')
    }
  }

  const handleDismissNotification = async (notificationId) => {
    try {
      await markStylistNotificationRead(notificationId)
      await loadLive()
    } catch {
      // ignore
    }
  }

  const handleBlockoutRequest = async (event) => {
    event.preventDefault()
    setError('')
    try {
      await requestStylistBlockout(blockoutForm)
      setBlockoutForm({ title: 'Time off', starts_at: '', ends_at: '', notes: '' })
      setTab('today')
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to submit time-off request.'))
    }
  }

  const stylistName = schedule?.stylist?.name ?? 'Stylist'

  return (
    <div className="mx-auto min-h-[calc(100vh-4rem)] max-w-lg bg-background pb-24">
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/95 px-4 py-4 backdrop-blur">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Stylist workspace</p>
        <h1 className="text-xl font-semibold">{stylistName}</h1>
        <p className="text-xs text-muted-foreground">
          {schedule?.business_date ?? 'Today'} · updates every {pollSeconds}s
        </p>
      </header>

      {(live?.unread_notifications ?? []).length > 0 ? (
        <div className="space-y-2 border-b border-amber-200/60 bg-amber-50/80 px-4 py-3">
          {live.unread_notifications.map((item) => (
            <div key={item.id} className="flex items-start gap-2 text-sm text-amber-950">
              <Bell className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <p>{item.message}</p>
                <button
                  type="button"
                  className="mt-1 text-xs font-semibold text-amber-800 underline"
                  onClick={() => handleDismissNotification(item.id)}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <nav className="flex gap-1 overflow-x-auto border-b border-border/50 px-2 py-2">
        {TABS.map((item) => {
          const Icon = item.icon
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold',
                tab === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {item.label}
            </button>
          )
        })}
      </nav>

      <div className="px-4 py-4">
        {error ? (
          <p className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        ) : null}

        {loading && tab === 'today' ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading schedule…
          </div>
        ) : null}

        {tab === 'today' && !loading ? (
          <div className="space-y-3">
            {(schedule?.appointments ?? []).length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border/70 px-4 py-10 text-center text-sm text-muted-foreground">
                No appointments on your board today.
              </p>
            ) : (
              schedule.appointments.map((appointment) => (
                <article key={appointment.id} className="rounded-2xl border border-border/60 bg-card p-4 shadow-luxury-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{appointment.customer?.name ?? 'Guest'}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatArrivalTime(appointment.starts_at)} · {appointment.status}
                      </p>
                    </div>
                    <Scissors className="h-4 w-4 text-primary" aria-hidden />
                  </div>
                  <ul className="mt-3 space-y-2">
                    {(appointment.services ?? []).map((line) => (
                      <li key={line.id} className="rounded-xl bg-muted/30 px-3 py-2 text-sm">
                        <p className="font-medium">{line.service?.name ?? 'Service'}</p>
                        <p className="text-[11px] capitalize text-muted-foreground">
                          Status: {line.line_status ?? 'waiting'}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {line.line_status !== 'in_chair' && line.line_status !== 'done' ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={updatingId !== ''}
                              onClick={() => handleServiceStatus(appointment.id, line.id, 'in_chair')}
                            >
                              Start
                            </Button>
                          ) : null}
                          {line.line_status === 'in_chair' ? (
                            <Button
                              type="button"
                              size="sm"
                              disabled={updatingId !== ''}
                              onClick={() => handleServiceStatus(appointment.id, line.id, 'done')}
                            >
                              Done
                            </Button>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </article>
              ))
            )}

            {(live?.queue ?? []).length > 0 ? (
              <section className="rounded-2xl border border-border/60 bg-card p-4">
                <h2 className="text-sm font-semibold">Walk-in queue</h2>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {live.queue.map((entry) => (
                    <li key={entry.id}>
                      {entry.customer_name ?? 'Walk-in'} — {entry.service_name ?? 'Service'}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        ) : null}

        {tab === 'tips' ? (
          <TipsPanel tips={tips} period={period} onPeriodChange={setPeriod} />
        ) : null}

        {tab === 'commissions' ? (
          <CommissionsPanel commissions={commissions} period={period} onPeriodChange={setPeriod} />
        ) : null}

        {tab === 'time-off' ? (
          <form onSubmit={handleBlockoutRequest} className="space-y-3 rounded-2xl border border-border/60 bg-card p-4">
            <h2 className="text-sm font-semibold">Request time off</h2>
            <p className="text-xs text-muted-foreground">Manager must approve before it blocks the calendar.</p>
            <label className="block text-xs">
              Title
              <input
                className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm"
                value={blockoutForm.title}
                onChange={(e) => setBlockoutForm((c) => ({ ...c, title: e.target.value }))}
                required
              />
            </label>
            <label className="block text-xs">
              From
              <input
                type="datetime-local"
                className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm"
                value={blockoutForm.starts_at}
                onChange={(e) => setBlockoutForm((c) => ({ ...c, starts_at: e.target.value }))}
                required
              />
            </label>
            <label className="block text-xs">
              To
              <input
                type="datetime-local"
                className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm"
                value={blockoutForm.ends_at}
                onChange={(e) => setBlockoutForm((c) => ({ ...c, ends_at: e.target.value }))}
                required
              />
            </label>
            <Button type="submit" className="w-full">Submit request</Button>
          </form>
        ) : null}
      </div>

      <p className="px-4 text-center text-[11px] text-muted-foreground">
        Install on your tablet: browser menu → Add to Home Screen.
      </p>
    </div>
  )
}

/**
 * @param {{ tips: object | null, period: string, onPeriodChange: (id: string) => void }} props
 */
function TipsPanel({ tips, period, onPeriodChange }) {
  return (
    <div className="space-y-4">
      <PeriodPicker period={period} onPeriodChange={onPeriodChange} />
      {tips ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <StatCard label="Direct" value={`AED ${formatReportMoney(tips.summary?.direct_total)}`} />
            <StatCard label="Pool" value={`AED ${formatReportMoney(tips.summary?.pool_total)}`} />
          </div>
          <ul className="space-y-2 text-sm">
            {(tips.entries ?? []).map((entry) => (
              <li key={entry.id} className="rounded-xl border border-border/60 px-3 py-2">
                AED {formatReportMoney(entry.amount)} · {entry.tip_type} · {entry.status}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No tip data for this period.</p>
      )}
    </div>
  )
}

/**
 * @param {{ commissions: object | null, period: string, onPeriodChange: (id: string) => void }} props
 */
function CommissionsPanel({ commissions, period, onPeriodChange }) {
  const report = commissions?.report
  return (
    <div className="space-y-4">
      <PeriodPicker period={period} onPeriodChange={onPeriodChange} />
      {report ? (
        <>
          <StatCard
            label="Commission total"
            value={`AED ${formatReportMoney(report.summary?.commission_total)}`}
          />
          <ul className="space-y-2 text-sm">
            {(report.entries ?? []).map((entry) => (
              <li key={entry.id} className="rounded-xl border border-border/60 px-3 py-2">
                {entry.sale_number} · AED {formatReportMoney(entry.commission_amount)}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No commission entries for this period.</p>
      )}
    </div>
  )
}

/**
 * @param {{ period: string, onPeriodChange: (id: string) => void }} props
 */
function PeriodPicker({ period, onPeriodChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {REPORT_PERIOD_OPTIONS.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onPeriodChange(option.id)}
          className={cn(
            'rounded-lg px-2.5 py-1 text-xs font-medium',
            period === option.id ? 'bg-primary text-primary-foreground' : 'bg-muted/70',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function StatCard({ label, value }) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
      <p className="text-[10px] uppercase text-muted-foreground">{label}</p>
      <p className="text-base font-semibold">{value}</p>
    </div>
  )
}
